const ALLOWED_ICON_PATH_REGEXP = /^\/images\/payment\/(drivers|methods)\/[^/]+\.svg$/i;

/**
 * Checks whether a URL origin belongs to a local development endpoint.
 *
 * Recognises standard loopback addresses and the `.test` TLD which is
 * reserved by IANA for local testing (RFC 6761).
 */
function isLocalDevOrigin(url: URL): boolean {
  return (
    'localhost' === url.hostname ||
    '127.0.0.1' === url.hostname ||
    '[::1]' === url.hostname ||
    url.hostname.endsWith('.test')
  );
}

/**
 * Validates API supplied icon URLs against backend origin and static icon paths.
 *
 * Returns false for any malformed input or malformed backend configuration.
 */
export function isAllowedIconUrl(url: string): boolean {
  const backendBaseUrl = process.env.REACT_APP_BACKEND_BASE_URL;

  if (!backendBaseUrl) {
    return false;
  }

  try {
    const expectedUrl = new URL(backendBaseUrl);
    const candidateUrl = new URL(url);
    const isExpectedLocalDev = isLocalDevOrigin(expectedUrl);
    const isCandidateLocalDev = isLocalDevOrigin(candidateUrl);

    if ('http:' !== candidateUrl.protocol && 'https:' !== candidateUrl.protocol) {
      return false;
    }

    if ('http:' === candidateUrl.protocol && (!isExpectedLocalDev || !isCandidateLocalDev)) {
      return false;
    }

    const hasExactOriginMatch = expectedUrl.origin === candidateUrl.origin;
    const isAllowedLocalDevHttpOrigin =
      'http:' === candidateUrl.protocol &&
      'http:' === expectedUrl.protocol &&
      isExpectedLocalDev &&
      isCandidateLocalDev &&
      expectedUrl.hostname === candidateUrl.hostname &&
      expectedUrl.port === candidateUrl.port;

    if (!hasExactOriginMatch && !isAllowedLocalDevHttpOrigin) {
      return false;
    }

    if (candidateUrl.search || candidateUrl.hash) {
      return false;
    }

    return ALLOWED_ICON_PATH_REGEXP.test(candidateUrl.pathname);
  } catch {
    return false;
  }
}
